/**
 * usePromptsLibrary — localStorage-backed library of reusable prompts.
 *
 * MVP: per-browser. Migracja do DB analogicznie do useAvatars w osobnym sprincie
 * gdy będzie potrzeba sharingu między urządzeniami / kontami agencyjnymi.
 */
import { useCallback, useEffect, useState } from "react";

export interface SavedPrompt {
  id: string;
  text: string;
  label: string;
  tags: string[];
  starred?: boolean;
  createdAt: string;
}

const STORAGE_KEY = "viralpartners.savedPrompts.v1";

function loadFromStorage(): SavedPrompt[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (p): p is SavedPrompt =>
        typeof p === "object" &&
        p !== null &&
        typeof (p as SavedPrompt).id === "string" &&
        typeof (p as SavedPrompt).text === "string"
    );
  } catch {
    return [];
  }
}

function saveToStorage(prompts: SavedPrompt[]): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prompts));
}

export function usePromptsLibrary() {
  const [prompts, setPrompts] = useState<SavedPrompt[]>(() => loadFromStorage());

  useEffect(() => {
    saveToStorage(prompts);
  }, [prompts]);

  const savePrompt = useCallback(
    (input: { text: string; label?: string; tags?: string[] }): SavedPrompt => {
      const trimmed = input.text.trim();
      const newPrompt: SavedPrompt = {
        id: `prompt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        text: trimmed,
        label: input.label?.trim() || trimmed.slice(0, 60),
        tags: input.tags ?? [],
        createdAt: new Date().toISOString(),
      };
      setPrompts((prev) => [newPrompt, ...prev]);
      return newPrompt;
    },
    []
  );

  const deletePrompt = useCallback((id: string): void => {
    setPrompts((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const updatePrompt = useCallback(
    (id: string, patch: Partial<Omit<SavedPrompt, "id" | "createdAt">>): void => {
      setPrompts((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
    },
    []
  );

  const importBulk = useCallback(
    (
      items: Array<Pick<SavedPrompt, "text"> & Partial<Pick<SavedPrompt, "label" | "tags">>>
    ): number => {
      const existingTexts = new Set(prompts.map((p) => p.text));
      const additions: SavedPrompt[] = [];
      for (const item of items) {
        const trimmed = item.text?.trim();
        if (!trimmed || existingTexts.has(trimmed)) continue;
        existingTexts.add(trimmed);
        additions.push({
          id: `prompt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${additions.length}`,
          text: trimmed,
          label: item.label?.trim() || trimmed.slice(0, 60),
          tags: Array.isArray(item.tags) ? item.tags : [],
          createdAt: new Date().toISOString(),
        });
      }
      if (additions.length === 0) return 0;
      setPrompts((prev) => [...additions, ...prev]);
      return additions.length;
    },
    [prompts]
  );

  return { prompts, savePrompt, deletePrompt, updatePrompt, importBulk };
}
